import { Empty, Flex, Typography } from "antd";
import { Fragment, type ReactNode } from "react";

function sanitizePreviewLink(href: string) {
  return /^https?:\/\//i.test(href) ? href : "#";
}

function renderInlineMarkdown(text: string, keyPrefix: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  const pattern = /(`[^`]+`)|(\*\*[^*]+\*\*)|(\*[^*]+\*)|(\[[^\]]+\]\([^)]+\))/g;
  let cursor = 0;
  let match: RegExpExecArray | null = pattern.exec(text);
  while (match) {
    if (match.index > cursor) {
      nodes.push(text.slice(cursor, match.index));
    }
    const token = match[0];
    if (token === undefined) {
      break;
    }
    if (token.startsWith("`")) {
      nodes.push(<code key={`${keyPrefix}-code-${match.index}`}>{token.slice(1, -1)}</code>);
    } else if (token.startsWith("**")) {
      nodes.push(<strong key={`${keyPrefix}-strong-${match.index}`}>{token.slice(2, -2)}</strong>);
    } else if (token.startsWith("*")) {
      nodes.push(<em key={`${keyPrefix}-em-${match.index}`}>{token.slice(1, -1)}</em>);
    } else {
      const linkMatch = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(token);
      if (linkMatch && linkMatch[1] !== undefined && linkMatch[2] !== undefined) {
        nodes.push(
          <a
            key={`${keyPrefix}-link-${match.index}`}
            href={sanitizePreviewLink(linkMatch[2])}
            rel="noreferrer"
            target="_blank"
          >
            {linkMatch[1]}
          </a>,
        );
      } else {
        nodes.push(token);
      }
    }
    cursor = match.index + token.length;
    match = pattern.exec(text);
  }
  if (cursor < text.length) {
    nodes.push(text.slice(cursor));
  }
  return nodes.length > 0 ? nodes : [text];
}

export function renderMarkdownPreview(
  value: string,
  previewHeight: string,
  emptyPreviewText: string,
): ReactNode {
  const normalized = value.replace(/\r\n/g, "\n").trim();
  if (!normalized) {
    return (
      <Flex align="center" justify="center" style={{ minHeight: previewHeight, padding: 16 }}>
        <Empty description={emptyPreviewText} image={Empty.PRESENTED_IMAGE_SIMPLE} />
      </Flex>
    );
  }

  const lines = normalized.split("\n");
  const blocks: ReactNode[] = [];
  let index = 0;

  while (index < lines.length) {
    const line = lines[index];
    if (line === undefined) {
      break;
    }
    const trimmed = line.trim();
    if (!trimmed) {
      index += 1;
      continue;
    }
    const headingMatch = /^(#{1,6})\s+(.+)$/.exec(trimmed);
    if (headingMatch) {
      const heading = headingMatch[1];
      const headingText = headingMatch[2];
      if (heading === undefined || headingText === undefined) {
        index += 1;
        continue;
      }
      const level = Math.min(5, heading.length + 1) as 1 | 2 | 3 | 4 | 5;
      blocks.push(
        <Typography.Title
          key={`heading-${index}`}
          level={level}
          style={{ marginTop: 0, marginBottom: 12 }}
        >
          {renderInlineMarkdown(headingText, `heading-${index}`)}
        </Typography.Title>,
      );
      index += 1;
      continue;
    }
    if (trimmed.startsWith("```")) {
      const codeLines: string[] = [];
      index += 1;
      while (index < lines.length) {
        const codeLine = lines[index];
        if (codeLine === undefined || codeLine.trim().startsWith("```")) break;
        codeLines.push(codeLine);
        index += 1;
      }
      if (index < lines.length) {
        index += 1;
      }
      blocks.push(
        <pre
          key={`code-${index}`}
          style={{
            margin: 0,
            padding: 12,
            borderRadius: 8,
            overflowX: "auto",
            whiteSpace: "pre-wrap",
          }}
        >
          <code>{codeLines.join("\n")}</code>
        </pre>,
      );
      continue;
    }
    if (/^>\s?/.test(trimmed)) {
      const quoteLines: string[] = [];
      while (index < lines.length) {
        const quoteLine = lines[index];
        if (quoteLine === undefined || !/^>\s?/.test(quoteLine.trim())) break;
        quoteLines.push(quoteLine.trim().replace(/^>\s?/, ""));
        index += 1;
      }
      blocks.push(
        <blockquote
          key={`quote-${index}`}
          style={{
            margin: 0,
            paddingLeft: 12,
            borderLeft: "3px solid currentColor",
            opacity: 0.8,
            whiteSpace: "pre-wrap",
          }}
        >
          {renderInlineMarkdown(quoteLines.join("\n"), `quote-${index}`)}
        </blockquote>,
      );
      continue;
    }
    const unorderedListMatch = /^[-*]\s+(.+)$/.exec(trimmed);
    const orderedListMatch = /^(\d+)\.\s+(.+)$/.exec(trimmed);
    if (unorderedListMatch || orderedListMatch) {
      const ordered = Boolean(orderedListMatch);
      const items: string[] = [];
      while (index < lines.length) {
        const rawListLine = lines[index];
        if (rawListLine === undefined) break;
        const listLine = rawListLine.trim();
        const currentMatch = ordered
          ? /^(\d+)\.\s+(.+)$/.exec(listLine)
          : /^[-*]\s+(.+)$/.exec(listLine);
        if (!currentMatch) {
          break;
        }
        const item = currentMatch[ordered ? 2 : 1];
        if (item === undefined) break;
        items.push(item);
        index += 1;
      }
      const ListTag = ordered ? "ol" : "ul";
      blocks.push(
        <ListTag key={`list-${index}`} style={{ margin: 0, paddingLeft: 20 }}>
          {items.map((item, itemIndex) => (
            <li key={`list-item-${index}-${itemIndex}`} style={{ marginBottom: 6 }}>
              {renderInlineMarkdown(item, `list-${index}-${itemIndex}`)}
            </li>
          ))}
        </ListTag>,
      );
      continue;
    }
    const paragraphLines: string[] = [];
    while (index < lines.length) {
      const paragraphLine = lines[index];
      if (paragraphLine === undefined) break;
      const paragraphTrimmed = paragraphLine.trim();
      if (
        !paragraphTrimmed ||
        /^(#{1,6})\s+/.test(paragraphTrimmed) ||
        paragraphTrimmed.startsWith("```") ||
        /^>\s?/.test(paragraphTrimmed) ||
        /^[-*]\s+/.test(paragraphTrimmed) ||
        /^\d+\.\s+/.test(paragraphTrimmed)
      ) {
        break;
      }
      paragraphLines.push(paragraphLine);
      index += 1;
    }
    blocks.push(
      <Typography.Paragraph
        key={`paragraph-${index}`}
        style={{ marginBottom: 0, whiteSpace: "pre-wrap" }}
      >
        {renderInlineMarkdown(paragraphLines.join("\n"), `paragraph-${index}`).map(
          (item, itemIndex) => (
            <Fragment key={`paragraph-fragment-${index}-${itemIndex}`}>{item}</Fragment>
          ),
        )}
      </Typography.Paragraph>,
    );
  }

  return (
    <Flex vertical gap={12}>
      {blocks}
    </Flex>
  );
}
